import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { CheckCircle, Circle, X, Building, Calendar, Image, CreditCard, Users, ArrowRight, BookOpen } from 'lucide-react';
import { toast } from 'sonner';

import axiosInstance from '../../../services/AxiosInstance';
import { getApiUrl } from '../../../utils/ApiUtils';

const CHECKLIST_ITEMS = [
    { key: 'companyDetails', label: 'Företagsuppgifter', icon: Building, settingsTab: 'company' },
    { key: 'fiscalYear', label: 'Räkenskapsår', icon: Calendar, settingsTab: 'bookkeeping' },
    // UX_PLAN.md P8: the step after setup is the first bookkeeping — done the
    // moment the company has one verifikat (manual, imported or matched).
    { key: 'firstVerification', label: 'Första bokföringen', icon: BookOpen, route: 'create-bookkeeping-entry' },
    { key: 'logo', label: 'Logotyp', icon: Image, settingsTab: 'company' },
    { key: 'invoiceSettings', label: 'Betalningsuppgifter', icon: CreditCard, settingsTab: 'invoicing' },
    { key: 'teamMembers', label: 'Teammedlemmar', icon: Users, settingsTab: 'users' },
];

const OnboardingChecklist = ({ companyId }) => {
    const history = useHistory();
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dismissed, setDismissed] = useState(false);
    // LEADS to it (verify-pass finding F1 — it was only reachable via Inställningar → Möt
    // teamet). null = feature off/unknown ⇒ the row simply doesn't render.

    useEffect(() => {
        let cancelled = false;

        const fetchStatus = async () => {
            try {
                const url = getApiUrl(`${companyId}/onboarding/status`);
                const response = await axiosInstance.get(url);
                if (!cancelled) setStatus(response.data);
            } catch (error) {
                if (!cancelled) console.error('Error fetching onboarding status:', error);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchStatus();
        return () => { cancelled = true; };
    }, [companyId]);

    const handleDismiss = async () => {
        try {
            const url = getApiUrl(`${companyId}/onboarding/dismiss-checklist`);
            await axiosInstance.post(url);
            setDismissed(true);
        } catch (error) {
            console.error('Error dismissing checklist:', error);
            toast.error('Kunde inte dölja checklistan');
        }
    };

    // Map each checklist item to its settings section (and card anchor).
    const ITEM_TARGETS = {
        companyDetails: 'company#grunduppgifter',
        fiscalYear: 'bookkeeping#rakenskapsar',
        logo: 'company',
        invoiceSettings: 'invoice#betalningsmetoder',
        teamMembers: 'users#aktiva-anvandare',
    };
    const handleItemClick = (item) => {
        if (item.route) {
            history.push(`/${companyId}/${item.route}`);
            return;
        }
        const target = ITEM_TARGETS[item.key] || item.settingsTab || 'company';
        history.push(`/${companyId}/settings/${target}`);
    };

    // Don't render if: loading, dismissed, not yet completed wizard, all items complete, or explicitly dismissed
    if (loading || dismissed) return null;
    if (!status) return null;
    if (!status.wizardCompleted) return null;
    if (status.checklistDismissed) return null;
    if (status.completedCount >= status.totalCount) return null;

    const progressPercent = Math.round((status.completedCount / status.totalCount) * 100);

    return (
        <>
            <style>{`
                .onboarding-checklist {
                    background: #fff;
                    border-radius: 8px;
                    border: 1px solid #d7dae3;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
                    margin-bottom: 1.5rem;
                    padding: 1.25rem 1.5rem;
                }
                .onboarding-checklist-top {
                    display: flex;
                    align-items: flex-start;
                    justify-content: space-between;
                    margin-bottom: 1rem;
                }
                .onboarding-checklist-top-left {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                }
                .onboarding-checklist-icon {
                    width: 40px;
                    height: 40px;
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: var(--rgba-primary-2);
                    color: var(--primary);
                    flex-shrink: 0;
                }
                .onboarding-checklist-title {
                    font-size: 0.95rem;
                    font-weight: 600;
                    color: #333;
                    margin: 0 0 0.125rem 0;
                }
                .onboarding-checklist-subtitle {
                    font-size: 0.8rem;
                    color: #64748b;
                    margin: 0;
                }
                .onboarding-checklist-dismiss {
                    background: none;
                    border: none;
                    color: #94a3b8;
                    cursor: pointer;
                    padding: 4px;
                    border-radius: 6px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.15s;
                    flex-shrink: 0;
                }
                .onboarding-checklist-dismiss:hover {
                    background: #f1f5f9;
                    color: #64748b;
                }
                .onboarding-checklist-progress-row {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    margin-bottom: 1rem;
                }
                .onboarding-checklist-progress-bar {
                    flex: 1;
                    height: 6px;
                    border-radius: 3px;
                    background: #f1f5f9;
                    overflow: hidden;
                }
                .onboarding-checklist-progress-fill {
                    height: 100%;
                    border-radius: 3px;
                    background: var(--primary);
                    transition: width 0.3s ease;
                }
                .onboarding-checklist-progress-label {
                    font-size: 0.75rem;
                    font-weight: 600;
                    color: var(--primary);
                    white-space: nowrap;
                }
                .onboarding-checklist-items {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 0.5rem;
                }
                .onboarding-checklist-item {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.5rem 0.75rem;
                    border-radius: 8px;
                    font-size: 0.8rem;
                    font-weight: 500;
                    background: #f8fafc;
                    border: 1px solid #f1f5f9;
                    cursor: pointer;
                    transition: all 0.15s;
                    color: #64748b;
                }
                .onboarding-checklist-item:hover {
                    background: #f1f5f9;
                    border-color: #e2e8f0;
                }
                .onboarding-checklist-item.completed {
                    background: var(--rgba-primary-1);
                    border-color: var(--rgba-primary-2);
                    color: var(--primary);
                    cursor: default;
                }
                .onboarding-checklist-item.pending .checklist-item-arrow {
                    color: #cbd5e1;
                    transition: transform 0.15s;
                }
                .onboarding-checklist-item.pending:hover .checklist-item-arrow {
                    color: var(--primary);
                    transform: translateX(2px);
                }
            `}</style>
            <div className="onboarding-checklist">
                <div className="onboarding-checklist-top">
                    <div className="onboarding-checklist-top-left">
                        <div className="onboarding-checklist-icon">
                            <CheckCircle size={20} />
                        </div>
                        <div>
                            <h6 className="onboarding-checklist-title">Slutför konfigurationen</h6>
                            <p className="onboarding-checklist-subtitle">
                                Slutför de återstående stegen för att få ut det mesta av LedgerLoop.
                            </p>
                        </div>
                    </div>
                    <button
                        className="onboarding-checklist-dismiss"
                        onClick={handleDismiss}
                        title="Dölj"
                    >
                        <X size={16} />
                    </button>
                </div>
                <div className="onboarding-checklist-progress-row">
                    <div className="onboarding-checklist-progress-bar">
                        <div
                            className="onboarding-checklist-progress-fill"
                            style={{ width: `${progressPercent}%` }}
                        />
                    </div>
                    <span className="onboarding-checklist-progress-label">
                        {status.completedCount}/{status.totalCount}
                    </span>
                </div>
                <div className="onboarding-checklist-items">
                    {CHECKLIST_ITEMS.filter((item) => status.steps && status.steps[item.key]).map(item => {
                        const stepStatus = status.steps[item.key];
                        const isCompleted = stepStatus?.completed;
                        return (
                            <div
                                key={item.key}
                                className={`onboarding-checklist-item ${isCompleted ? 'completed' : 'pending'}`}
                                onClick={() => !isCompleted && handleItemClick(item)}
                            >
                                {isCompleted ? (
                                    <CheckCircle size={14} />
                                ) : (
                                    <Circle size={14} />
                                )}
                                {item.label}
                                {!isCompleted && (
                                    <ArrowRight size={12} className="checklist-item-arrow" />
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </>
    );
};

export default OnboardingChecklist;
